import {
  Button,
  InputAmount,
  InputSelect,
  InputWrapper,
} from '@vrplatform/components';
import {
  showApiErrorNotification,
  useApiMutation,
  useApiQuery,
  useTeam,
} from '@vrplatform/data';
import { Box, Group, LoadingOverlay, Modal } from '@mantine/core';
import { Text } from '@mantine/core';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import type { party_enum } from '@vrplatform/graphql';
import { getCurrencySymbol } from '@vrplatform/ui-common';
import type { SelectItem } from '@vrplatform/components';
import { bankersRound } from '@vrplatform/utils';

type Props = {
  opened: boolean;
  closeModal: () => void;
  listingId: string;
};

type FormInputs = {
  openingBalance: {
    centTotal: number;
    paidTo: party_enum;
  };
};

const PAID_TO_OPTIONS: SelectItem<FormInputs['openingBalance']['paidTo']>[] = [
  {
    label: 'due to owner',
    value: 'owners',
  },
  {
    label: 'due from owner',
    value: 'manager',
  },
];

export const ListingOpeningBalanceInputs = () => {
  const methods = useFormContext<FormInputs>();
  const [{ currency }] = useTeam();

  const currencySymbol = getCurrencySymbol(currency).replaceAll(
    /[A-Za-z]/g,
    ''
  );

  return (
    <Group align="flex-start">
      <Controller
        control={methods.control}
        name={`openingBalance.centTotal`}
        rules={{
          validate: (value) => {
            if (typeof value !== 'number') return 'Amount is required';
            if (value < 0) return 'Please input a positive number';

            return true;
          },
        }}
        render={({ field, fieldState }) => {
          return (
            <InputWrapper error={fieldState.error?.message} flex={1}>
              <InputAmount
                value={
                  field.value === undefined
                    ? ''
                    : Number(field.value || '0') / 100
                }
                setValue={(value) =>
                  field.onChange(value === '' ? '' : Number(value || 0) * 100)
                }
                debounce={0}
                decimalScale={2}
                error={!!fieldState.error?.message}
                placeholder="0.00"
                leftSection={<span>{currencySymbol}</span>}
                hideControls
              />
            </InputWrapper>
          );
        }}
      />
      <Controller
        control={methods.control}
        name={`openingBalance.paidTo`}
        render={({ field, fieldState }) => {
          const value =
            PAID_TO_OPTIONS.find((x) => x.value === field.value) ?? null;

          return (
            <InputWrapper error={fieldState.error?.message} flex={1}>
              <InputSelect
                type="single"
                value={value}
                setValue={(v) => v?.value && field.onChange(v.value)}
                data={{
                  options: PAID_TO_OPTIONS,
                }}
                dropdownProps={{
                  hideSearch: true,
                }}
              />
            </InputWrapper>
          );
        }}
      />
    </Group>
  );
};

function useListingOpeningBalanceQuery(listingId: string) {
  return useApiQuery(
    async (api, signal) => {
      const result = await api.GET('/listings/{id}/opening-balance', {
        params: {
          path: { id: listingId },
        },
        signal,
      });
      if (result.error) throw result.error;

      const centTotal = Math.abs(result.data.amount);

      return {
        openingBalance: {
          centTotal,
          paidTo: centTotal >= 0 ? 'owners' : 'manager',
        },
      } satisfies FormInputs;
    },
    {
      queryGroup: 'listings',
      uniqueQueryKey: 'listing-opening-balance-listings',
      queryKeys: ['listings-opening-balance-modal'],
      variables: {
        listingId,
      },
    }
  );
}

export const ListingOpeningBalanceModal = ({
  closeModal: cl,
  opened,
  listingId,
}: Props) => {
  const { data, isFetching } = useListingOpeningBalanceQuery(listingId);

  const { mutateAsync } = useApiMutation(
    'put',
    '/listings/{id}/opening-balance'
  );

  const methods = useForm<FormInputs>({
    values: data,
  });

  const submit = async (data: FormInputs) => {
    await mutateAsync({
      params: {
        path: {
          id: listingId,
        },
      },
      body: {
        amount: bankersRound(
          data.openingBalance.centTotal *
            (data.openingBalance.paidTo === 'owners' ? 1 : -1)
        ),
      },
    })
      .then(() => {
        cl();
      })
      .catch((error) => {
        showApiErrorNotification({
          error,
          title: 'Failed to update opening balance',
          defaultMessage:
            'Please try again later and contact support if the issue persists.',
        });
      });
  };

  const closeModal = () => {
    cl();
    setTimeout(() => {
      methods.reset(data);
    }, 100);
  };
  return (
    <Modal
      centered
      opened={opened}
      onClose={closeModal}
      title={'Update Starting Balance'}
      size={500}
      styles={{
        title: {
          fontWeight: 500,
        },
        body: {
          position: 'relative',
        },
      }}
    >
      <LoadingOverlay visible={isFetching} />

      <Text c="neutral" mb="md">
        You are able to update the starting balance for this listing which will
        set the starting point for your owner statements and ledger.
      </Text>

      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={methods.handleSubmit(submit)}
          onReset={closeModal}
        >
          <InputWrapper label="Starting Balance">
            <ListingOpeningBalanceInputs />
          </InputWrapper>

          <Group justify="right" mt="lg">
            <Button type="reset" disabled={methods.formState.isSubmitting}>
              Cancel
            </Button>

            <Button
              type="submit"
              variant="primary"
              loading={methods.formState.isSubmitting}
            >
              Submit
            </Button>
          </Group>
        </Box>
      </FormProvider>
    </Modal>
  );
};
